import {
  Alert,
  Button,
  DialogFooterV2,
  Icons,
  Inset,
  Typography,
  Stack,
} from '@cast/design-system';
import { K8sProvider } from '@cast/shared-types';

import { AgentCodeSnippet } from 'components/code';

import { ManageAutoCommitments } from './ManageAutoCommitments';
import { useCommitmentsViewMode } from '../../../_hooks/useCommitmentsViewMode';

type ScriptSetupSlideProps = {
  isMultiCloud: boolean;
  provider: K8sProvider | undefined;
  onNext: () => void;
  onBack: () => void;
  useQuery: () => {
    script?: string;
    isLoading: boolean;
    refetch: () => void;
    error: unknown;
  };
};

export const ScriptSetupSlide = ({
  onNext,
  onBack,
  isMultiCloud,
  provider,
  useQuery,
}: ScriptSetupSlideProps) => {
  const { script, isLoading, refetch, error } = useQuery();
  const commitmentsViewMode = useCommitmentsViewMode();

  return (
    <div data-testid={`${provider}-setup-slide`}>
      <Inset pb={16}>
        <Stack gap={8}>
          <Stack>
            <Typography variant="A1">
              Open your cloud shell or terminal and run the script
            </Typography>
            <Typography variant="P12R" color="textSecondary" sx={{ pb: 8 }}>
              Make sure you have access to your cloud account and permissions to
              read commitments data from billing service. Copy the script below
              and run it in your cloud shell or terminal.
            </Typography>
            <AgentCodeSnippet
              code={script}
              isLoading={isLoading || !script}
              refetch={refetch}
              error={error}
              testId="import-script"
            />
            <ManageAutoCommitments />
          </Stack>
          {commitmentsViewMode.type === 'child-org' && (
            <Alert
              color="info"
              size="sm"
              variant="filled"
              data-testid="child-org-sharing-alert"
            >
              Sharing is enabled by your admin. Uploaded commitments are shared
              with all other child-organizations by default.
            </Alert>
          )}
        </Stack>
      </Inset>
      <DialogFooterV2
        startContent={
          isMultiCloud ? (
            <Button
              variant="ghost"
              testId="back-button"
              startIcon={<Icons.ArrowLeft />}
              onClick={onBack}
            >
              Back
            </Button>
          ) : undefined
        }
        endContent={
          <Button
            variant="primary"
            onClick={onNext}
            testId="i-ran-the-script-button"
            disabled={!!error || isLoading}
          >
            I ran the script
          </Button>
        }
        divider
      />
    </div>
  );
};
